import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useModalBack from '../../hooks/useModalBack';
import { Tag, ChevronDown, X } from 'lucide-react';

// Emoji icon groups — ordered by most commonly used first
const ICON_GROUPS = {
  'Finance': [
    { name: '🏦', color: '#3b82f6' },
    { name: '💳', color: '#6366f1' },
    { name: '💵', color: '#22c55e' },
    { name: '💰', color: '#f59e0b' },
    { name: '🐷', color: '#ec4899' },
    { name: '🏛️', color: '#6366f1' },
    { name: '💸', color: '#10b981' },
    { name: '🧾', color: '#f59e0b' },
    { name: '💎', color: '#06b6d4' },
    { name: '📊', color: '#8b5cf6' },
    { name: '💹', color: '#22c55e' },
    { name: '🪙', color: '#eab308' },
  ],
  'Shopping': [
    { name: '🛒', color: '#f97316' },
    { name: '🛍️', color: '#ec4899' },
    { name: '🏪', color: '#8b5cf6' },
    { name: '🎁', color: '#ef4444' },
    { name: '🏷️', color: '#6366f1' },
    { name: '👗', color: '#ec4899' },
    { name: '👟', color: '#3b82f6' },
    { name: '👜', color: '#d946ef' },
  ],
  'Food & Dining': [
    { name: '🍽️', color: '#f97316' },
    { name: '☕', color: '#78350f' },
    { name: '🍕', color: '#ef4444' },
    { name: '🍔', color: '#f59e0b' },
    { name: '🥗', color: '#22c55e' },
    { name: '🍜', color: '#f97316' },
    { name: '🧁', color: '#ec4899' },
    { name: '🍺', color: '#eab308' },
    { name: '🥤', color: '#ef4444' },
  ],
  'Transport': [
    { name: '🚗', color: '#3b82f6' },
    { name: '✈️', color: '#0ea5e9' },
    { name: '🚌', color: '#f59e0b' },
    { name: '🚃', color: '#6366f1' },
    { name: '⛽', color: '#ef4444' },
    { name: '🏍️', color: '#64748b' },
    { name: '🚕', color: '#eab308' },
    { name: '🚲', color: '#22c55e' },
    { name: '🛵', color: '#f97316' },
    { name: '🚐', color: '#8b5cf6' },
  ],
  'Bills & Utilities': [
    { name: '⚡', color: '#eab308' },
    { name: '📱', color: '#6366f1' },
    { name: '📶', color: '#3b82f6' },
    { name: '💧', color: '#0ea5e9' },
    { name: '🔥', color: '#ef4444' },
    { name: '🏠', color: '#6366f1' },
    { name: '📺', color: '#0f172a' },
    { name: '💻', color: '#64748b' },
    { name: '🌐', color: '#3b82f6' },
  ],
  'Health & Fitness': [
    { name: '❤️', color: '#ef4444' },
    { name: '🏥', color: '#0ea5e9' },
    { name: '💊', color: '#22c55e' },
    { name: '🏋️', color: '#f97316' },
    { name: '🧘', color: '#8b5cf6' },
    { name: '💆', color: '#ec4899' },
    { name: '🩺', color: '#0ea5e9' },
    { name: '🦷', color: '#f1f5f9' },
  ],
  'Entertainment': [
    { name: '🎬', color: '#ef4444' },
    { name: '🎵', color: '#ec4899' },
    { name: '🎮', color: '#8b5cf6' },
    { name: '🎭', color: '#f59e0b' },
    { name: '🎪', color: '#f97316' },
    { name: '📖', color: '#6366f1' },
    { name: '🎯', color: '#ef4444' },
    { name: '🎲', color: '#22c55e' },
  ],
  'Education': [
    { name: '🎓', color: '#0f172a' },
    { name: '📚', color: '#f59e0b' },
    { name: '✏️', color: '#64748b' },
    { name: '🎒', color: '#3b82f6' },
    { name: '🔬', color: '#6366f1' },
  ],
  'Travel': [
    { name: '🌍', color: '#22c55e' },
    { name: '🏖️', color: '#0ea5e9' },
    { name: '🏔️', color: '#64748b' },
    { name: '🗺️', color: '#f59e0b' },
    { name: '🧳', color: '#78350f' },
    { name: '🏨', color: '#6366f1' },
  ],
  'Income': [
    { name: '💰', color: '#22c55e' },
    { name: '💵', color: '#22c55e' },
    { name: '📈', color: '#10b981' },
    { name: '💼', color: '#64748b' },
    { name: '🏢', color: '#6366f1' },
    { name: '👨‍💻', color: '#3b82f6' },
  ],
  'Family & Pets': [
    { name: '👶', color: '#ec4899' },
    { name: '🐶', color: '#f59e0b' },
    { name: '🐱', color: '#f97316' },
    { name: '👨‍👩‍👧', color: '#6366f1' },
    { name: '🐾', color: '#a3e635' },
  ],
  'Nature': [
    { name: '🌿', color: '#22c55e' },
    { name: '☀️', color: '#f59e0b' },
    { name: '🌙', color: '#6366f1' },
    { name: '⭐', color: '#eab308' },
    { name: '🌸', color: '#ec4899' },
  ],
  'Other': [
    { name: '📌', color: '#ef4444' },
    { name: '🔧', color: '#64748b' },
    { name: '📦', color: '#78350f' },
    { name: '🏷️', color: '#6366f1' },
    { name: '➕', color: '#6b7280' },
  ],
};

// Legacy Lucide icon name → emoji mapping (for existing data)
const LEGACY_ICON_MAP = {
  'wallet': '💳',
  'credit-card': '💳',
  'banknote': '💵',
  'piggy-bank': '🐷',
  'landmark': '🏛️',
  'building-2': '🏦',
  'circle-dollar-sign': '💰',
  'badge-dollar-sign': '💰',
  'receipt': '🧾',
  'shopping-cart': '🛒',
  'shopping-bag': '🛍️',
  'store': '🏪',
  'gift': '🎁',
  'tag': '🏷️',
  'car': '🚗',
  'plane': '✈️',
  'train': '🚃',
  'bus': '🚌',
  'fuel': '⛽',
  'utensils': '🍽️',
  'coffee': '☕',
  'home': '🏠',
  'zap': '⚡',
  'droplets': '💧',
  'flame': '🔥',
  'wifi': '📶',
  'smartphone': '📱',
  'laptop': '💻',
  'tv': '📺',
  'gamepad-2': '🎮',
  'music': '🎵',
  'film': '🎬',
  'book': '📖',
  'graduation-cap': '🎓',
  'briefcase': '💼',
  'building': '🏢',
  'heart': '❤️',
  'stethoscope': '🩺',
  'pill': '💊',
  'dumbbell': '🏋️',
  'scissors': '✂️',
  'shirt': '👗',
  'watch': '⌚',
  'gem': '💎',
  'baby': '👶',
  'dog': '🐶',
  'leaf': '🌿',
  'sun': '☀️',
  'moon': '🌙',
  'star': '⭐',
  'more-horizontal': '📌',
  'heart-pulse': '🩺',
  'lightbulb': '⚡',
  'sparkles': '💆',
  'shield': '🛡️',
  'repeat': '📺',
  'circle-dot': '📌',
  'trending-up': '📈',
  'percent': '🐷',
  'coins': '💹',
  'rotate-ccw': '🧾',
};

// Resolve icon name: convert legacy Lucide names → emoji
export const resolveIconName = (iconName) => {
  if (!iconName) return '🏷️';
  // Already an emoji (starts with non-ASCII)
  if (iconName.codePointAt(0) > 127) return iconName;
  // Legacy Lucide name → emoji
  return LEGACY_ICON_MAP[iconName] || '🏷️';
};

// EmojiIcon component — works as a drop-in replacement for Lucide icons
// Accepts className for sizing and ignores SVG-specific props (style, fill, etc.)
function EmojiIcon({ emoji, className = '' }) {
  // Extract size from className (e.g., "w-5 h-5" → font-size mapping)
  // Tailwind w-5 = 20px, w-6 = 24px, w-[22px] = 22px, etc.
  let fontSize = '1.25rem'; // default ~20px
  if (className.includes('w-3') || className.includes('h-3')) fontSize = '0.875rem';
  else if (className.includes('w-4') || className.includes('h-4')) fontSize = '1rem';
  else if (className.includes('w-5') || className.includes('h-5')) fontSize = '1.25rem';
  else if (className.includes('w-6') || className.includes('h-6')) fontSize = '1.5rem';
  else if (className.includes('w-7') || className.includes('h-7')) fontSize = '1.75rem';
  else if (className.includes('w-8') || className.includes('h-8')) fontSize = '2rem';
  else if (className.includes('w-[22px]')) fontSize = '1.375rem';

  return (
    <span
      role="img"
      style={{ fontSize, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {emoji}
    </span>
  );
}

// getIconComponent returns a React component — handles both emoji and legacy Lucide names
// Usage: const Icon = getIconComponent('🏦'); → <Icon className="w-5 h-5" />
export const getIconComponent = (iconName) => {
  const emoji = resolveIconName(iconName);
  // Return a component that renders the emoji
  const IconComponent = (props) => <EmojiIcon emoji={emoji} {...props} />;
  IconComponent.displayName = `EmojiIcon(${emoji})`;
  return IconComponent;
};

// Get default color for an icon
export const getIconDefaultColor = (iconName) => {
  const emoji = resolveIconName(iconName);
  for (const icons of Object.values(ICON_GROUPS)) {
    const found = icons.find(i => i.name === emoji);
    if (found) return found.color;
  }
  return '#6366f1';
};

export default function IconPicker({ value, onChange, onColorChange, color = '#6366f1' }) {
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = useCallback(() => setIsOpen(false), []);
  useModalBack(isOpen, closeModal, 'IconPicker');

  const resolvedValue = resolveIconName(value);

  const handleSelect = (iconName, iconColor) => {
    onChange(iconName);
    if (onColorChange && iconColor) {
      onColorChange(iconColor);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3 border border-surface-200 rounded-xl hover:border-surface-300 transition-colors bg-white"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{resolvedValue}</span>
        </div>
        <span className="flex-1 text-left text-sm text-surface-600 font-medium">
          Icon
        </span>
        <ChevronDown className={`w-5 h-5 text-surface-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm z-[100] flex items-end md:items-center justify-center md:p-4"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-md max-h-[85vh] md:max-h-[80vh] flex flex-col animate-slide-up md:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
              <h3 className="text-lg font-semibold text-surface-900">Select Icon</h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Icons Grid */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {Object.entries(ICON_GROUPS).map(([groupName, icons]) => (
                <div key={groupName}>
                  <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2 px-1">
                    {groupName}
                  </h4>
                  <div className="grid grid-cols-6 gap-2">
                    {icons.map(({ name, color: iconColor }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleSelect(name, iconColor)}
                        className={`aspect-square flex items-center justify-center rounded-xl text-2xl transition-all ${
                          resolvedValue === name
                            ? 'ring-2 ring-offset-1 bg-surface-100'
                            : 'hover:bg-surface-100 active:scale-90'
                        }`}
                        style={resolvedValue === name ? { '--tw-ring-color': iconColor } : undefined}
                        title={name}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
